import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { flushSync } from 'react-dom';
import DOMPurify from 'dompurify';
import styles from '../../styles/formComponent/AutoSuggest.module.scss';

const AutoSuggest = ({
  label,
  name,
  value = '',
  onChange,
  onBlur,
  onFocus,
  placeholder,
  required = false,
  disabled = false,
  error,
  helperText,
  variant = 'outlined',
  size = 'medium',
  options = [],
  multiple = false,
  freeSolo = false,
  loading = false,
  noOptionsText = 'No options',
  loadingText = 'Loading...',
  filterOptions,
  getOptionLabel,
  getOptionValue,
  isOptionEqualToValue,
  renderOption,
  showCheckboxes = false,
  className = '',
  autoFocus = false,
  clearOnBlur = false,
  clearOnEscape = true,
  disableClearable = false,
  openOnFocus = false,
  onInputChange,
  onDropdownScroll,
  onKeyboardNavigation,
  allowClear = true,
  notifyInputChangeOnClear = false,
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedValues, setSelectedValues] = useState(multiple ? (Array.isArray(value) ? value : []) : []);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const optionRefs = useRef([]);
  const dropdownContainerRef = useRef(null);
  const isTypingRef = useRef(false);
  const typingTimeoutRef = useRef(null);
  const isOpenRef = useRef(false);
  isOpenRef.current = isOpen;

  // Helper functions with defaults
  const defaultGetOptionLabel = useCallback((option) => {
    if (typeof option === 'string') return option;
    return option?.label || option?.name || String(option);
  }, []);

  const defaultGetOptionValue = useCallback((option) => {
    if (typeof option === 'string') return option;
    return option?.value || option?.id || option;
  }, []);

  const defaultIsOptionEqualToValue = useCallback((option, value) => {
    return defaultGetOptionValue(option) === defaultGetOptionValue(value);
  }, [defaultGetOptionValue]);

  const defaultFilterOptions = useCallback((options, inputValue) => {
    if (!inputValue) return options;
    return options.filter(option =>
      defaultGetOptionLabel(option).toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [defaultGetOptionLabel]);

  const calculateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      
      return {
        top: rect.bottom + scrollTop + 2,
        left: rect.left + scrollLeft,
        width: rect.width
      };
    }
    return { top: 0, left: 0, width: 0 };
  }, []);

  // Use provided functions or defaults - memoized to prevent re-renders
  const getLabel = useMemo(() => getOptionLabel || defaultGetOptionLabel, [getOptionLabel, defaultGetOptionLabel]);
  const getValue = useMemo(() => getOptionValue || defaultGetOptionValue, [getOptionValue, defaultGetOptionValue]);
  const isEqual = useMemo(() => isOptionEqualToValue || defaultIsOptionEqualToValue, [isOptionEqualToValue, defaultIsOptionEqualToValue]);
  const filter = useMemo(() => filterOptions || defaultFilterOptions, [filterOptions, defaultFilterOptions]);

  // Production-grade scroll function with no jerk effects
  const scrollToOption = useCallback((index) => {
    const optionElement = optionRefs.current[index];
    const dropdownContainer = dropdownContainerRef.current;
    
    if (!optionElement || !dropdownContainer) return;

    // Use scrollIntoView with precise control - most reliable method
    optionElement.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'auto'
    });
  }, []);

  // Initialize input value based on current value - only when not actively typing
  useEffect(() => {
    if (isTypingRef.current) return;
    // Don't wipe search text while the user has the dropdown open and nothing is selected.
    // options changing (from an API response) while they're typing would otherwise race-reset
    // the input back to "" via the setInputValue(value) line below.
    if (isOpenRef.current && !multiple && !value) return;
    
    setInputValue(value);
    if (!multiple && value) {
      const option = options.find(opt => isEqual(opt, value));
      const newInputValue = option ? getLabel(option) : (freeSolo ? String(value) : '');
      setInputValue(newInputValue);
    } else if (multiple) {
      setSelectedValues(Array.isArray(value) ? value : []);
      setInputValue('');
    }
  }, [value, options, multiple, freeSolo, getLabel, isEqual]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Handle click outside, scroll, and resize
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && 
        !inputRef.current.contains(event.target) &&
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target)
      ) {
        handleClose();
      }
    };

    const handleScroll = (event) => {
      if (isOpen) {
        // Don't reposition if scrolling within the dropdown itself
        if (dropdownRef.current && (
          dropdownRef.current.contains(event.target) ||
          event.target === dropdownRef.current
        )) {
          return;
        }
        
        // Only reposition if the scroll affects the input field's position
        requestAnimationFrame(() => {
          setDropdownPosition(calculateDropdownPosition());
        });
      }
    };

    const handleResize = () => {
      if (isOpen) {
        setDropdownPosition(calculateDropdownPosition());
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, calculateDropdownPosition]);

  const hasValue = multiple ? selectedValues.length > 0 : Boolean(inputValue || value);
  
  // Memoize filtered options to prevent unnecessary recalculations
  const filteredOptions = useMemo(() => {
    return filter(options, inputValue);
  }, [filter, options, inputValue]);

  const handleFocus = (e) => {
    setFocused(true);
    if (openOnFocus) {
      setDropdownPosition(calculateDropdownPosition());
      setIsOpen(true);
    }
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e) => {
    setFocused(false);
    
    // Clear typing timeout and reset flag on blur
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    isTypingRef.current = false;
    
    if (clearOnBlur && !multiple && !value) {
      setInputValue('');
    }
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleInputClick = () => {
    if (!isOpen) {
      setDropdownPosition(calculateDropdownPosition());
    }
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleInputChange = (e) => {
    const newInputValue = e.target.value;
    
    // Mark that user is actively typing
    isTypingRef.current = true;
    
    // Clear any existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Reset typing flag after user stops typing for 500ms
    typingTimeoutRef.current = setTimeout(() => {
      isTypingRef.current = false;
    }, 500);
    
    setInputValue(newInputValue);
    if (!isOpen) {
      setDropdownPosition(calculateDropdownPosition());
    }
    setIsOpen(true);
    setHighlightedIndex(-1);

    // Call the external onInputChange handler if provided
    if (onInputChange) {
      onInputChange(newInputValue);
    }

    if (freeSolo && !multiple) {
      const event = {
        target: {
          name,
          value: newInputValue
        }
      };
      if (onChange) {
        onChange(event);
      }
    }
  };

  const handleOptionClick = (option, event) => {
    // Prevent the click from triggering handleClickOutside
    if (event) {
      event.stopPropagation();
    }

    if(!allowClear) {
      setIsOpen(false);
    }
    
    // Reset typing flag when an option is selected
    isTypingRef.current = false;
    
    if (multiple) {
      const newValues = selectedValues.some(val => isEqual(val, option))
        ? selectedValues.filter(val => !isEqual(val, option))
        : [...selectedValues, option];
      
      if(allowClear) {
        setSelectedValues(newValues);
        setInputValue('');
      }
      
      const changeEvent = {
        target: {
          name,
          value: newValues
        }
      };
      if (onChange) {
        onChange(changeEvent);
      }
    } else {
      if(allowClear) {
        setInputValue(getLabel(option));
        setIsOpen(false);
      }
      
      const changeEvent = {
        target: {
          name,
          value: getValue(option)
        }
      };
      if (onChange) {
        onChange(changeEvent);
      }
    }
  };

  const handleClear = () => {
    // Check if clearing is allowed
    if (!allowClear) {
      onChange("");
      return; // Don't clear if not allowed
    }
    // Clear typing timeout and reset flag
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    isTypingRef.current = false;
    
    if (multiple) {
      setSelectedValues([]);
      const event = { target: { name, value: [] } };
      if (onChange) onChange(event);
    } else {
      setInputValue('');
      const event = { target: { name, value: '' } };
      if (onChange) onChange(event);
    }

    if (notifyInputChangeOnClear && onInputChange) {
      onInputChange('');
    }

    inputRef.current?.focus();
  };

  const handleRemoveChip = (optionToRemove) => {
    const newValues = selectedValues.filter(val => !isEqual(val, optionToRemove));
    setSelectedValues(newValues);
    
    const event = {
      target: {
        name,
        value: newValues
      }
    };
    if (onChange) {
      onChange(event);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setDropdownPosition(calculateDropdownPosition());
        setIsOpen(true);
      } else {
        const nextIndex = highlightedIndex < filteredOptions.length - 1 ? highlightedIndex + 1 : 0;
        flushSync(() => {
          setHighlightedIndex(nextIndex);
        });
        scrollToOption(nextIndex);
        
        if (onKeyboardNavigation) {
          onKeyboardNavigation('down', nextIndex, filteredOptions.length);
        }
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (isOpen) {
        const prevIndex = highlightedIndex > 0 ? highlightedIndex - 1 : filteredOptions.length - 1;
        flushSync(() => {
          setHighlightedIndex(prevIndex);
        });
        scrollToOption(prevIndex);
        
        if (onKeyboardNavigation) {
          onKeyboardNavigation('up', prevIndex, filteredOptions.length);
        }
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        const selectedOption = filteredOptions[highlightedIndex];
        // Don't allow selection of loading placeholder
        if (!selectedOption.isLoadingPlaceholder) {
          handleOptionClick(selectedOption);
        }
      } else if (!isOpen) {
        setDropdownPosition(calculateDropdownPosition());
        setIsOpen(true);
      }
    } else if (e.key === 'Escape') {
      if (isOpen) {
        handleClose();
        if (clearOnEscape && !value) {
          setInputValue('');
        }
      }
    } else if (e.key === 'Tab') {
      handleClose();
    }
  };

  const getInputClasses = () => {
    let classes = styles.inputField;
    
    if (error) classes += ` ${styles.error}`;
    if (hasValue) classes += ` ${styles.filled}`;
    if (label) classes += ` ${styles.hasLabel}`;
    if (isOpen) classes += ` ${styles.open}`;
    
    return classes;
  };

  const getLabelClasses = () => {
    let classes = styles.floatingLabel;
    
    if (focused || hasValue || placeholder) classes += ` ${styles.filled}`;
    if (focused) classes += ` ${styles.focused}`;
    if (error) classes += ` ${styles.error}`;
    if (disabled) classes += ` ${styles.disabled}`;
    
    return classes;
  };

  const getWrapperClasses = () => {
    let classes = `${styles.autoSuggestWrapper}`;
    if (variant === 'filled') classes += ` ${styles.filled}`;
    if (variant === 'outlined') classes += ` ${styles.outlined}`;
    if (size === 'small') classes += ` ${styles.small}`;
    if (className) classes += ` ${className}`;
    
    return classes;
  };

  const renderOptionContent = (option, index) => {
    if (renderOption) {
      return renderOption(option, { selected: multiple && selectedValues.some(val => isEqual(val, option)) });
    }

    const label = getLabel(option);
    const isSelected = multiple && selectedValues.some(val => isEqual(val, option));
    
    // Highlight matching text
    let highlightedLabel = label;
    if (inputValue) {
      const escapedInputValue = inputValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedInputValue})`, 'gi');
      highlightedLabel = label.replace(regex, '<span class="' + styles.optionMatch + '">$1</span>');
    }
    const sanitizedLabel = DOMPurify.sanitize(highlightedLabel);

    // Show checkboxes for multiselect when showCheckboxes prop is true
    if (multiple && showCheckboxes) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            checked={isSelected}
            readOnly
            style={{ margin: 0 }}
          />
          <span dangerouslySetInnerHTML={{ __html: sanitizedLabel }} />
        </div>
      );
    }

    return (
      <div className={styles.optionText}>
        <span dangerouslySetInnerHTML={{ __html: sanitizedLabel }} />
      </div>
    );
  };

  const DropdownPortal = () => {
    if (!isOpen) return null;

    const handleDropdownScroll = (e) => {
      // Prevent event bubbling to avoid repositioning
      e.stopPropagation();
      
      // Call the external onDropdownScroll handler if provided
      if (onDropdownScroll) {
        onDropdownScroll(e);
      }
    };

    const dropdownContent = (
      <div 
        ref={dropdownRef}
        className={styles.dropdownPortal}
        style={{
          position: 'absolute',
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          zIndex: 1050
        }}
        onScroll={handleDropdownScroll}
      >
        <div ref={dropdownContainerRef} className={styles.dropdown}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.loadingSpinner}></div>
              {loadingText}
            </div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const isSelected = multiple && selectedValues.some(val => isEqual(val, option));
              return (
                <div
                  key={getValue(option) || index}
                  ref={el => optionRefs.current[index] = el}
                  id={`option-${index}`}
                  className={`
                    ${styles.option} 
                    ${index === highlightedIndex ? styles.highlighted : ''} 
                    ${isSelected ? styles.selected : ''}
                  `}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent input blur
                    handleOptionClick(option, e);
                  }}
                  role="option"
                  aria-selected={isSelected}
                >
                  {renderOptionContent(option, index)}
                  {isSelected && multiple && !showCheckboxes && (
                    <div className={styles.checkIcon}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M20 6L9 17L4 12"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className={styles.noOptions}>{noOptionsText}</div>
          )}
        </div>
      </div>
    );

    return createPortal(dropdownContent, document.body);
  };

  return (
    <>
      <div className={getWrapperClasses()}>
        <div className={styles.inputContainer}>
          <input
            ref={inputRef}
            type="text"
            name={name}
            value={inputValue}
            onChange={handleInputChange}
            onClick={handleInputClick}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className={getInputClasses()}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-form-type="other"
            data-lpignore="true"
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            aria-activedescendant={highlightedIndex >= 0 ? `option-${highlightedIndex}` : undefined}
            {...props}
          />
          
          {!disableClearable && hasValue && !disabled && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={handleClear}
              tabIndex={-1}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
          
          <div className={`${styles.dropdownIcon} ${isOpen ? styles.open : ''} ${disabled ? styles.disabled : ''}`}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M7 10L12 15L17 10H7Z"
                fill="currentColor"
              />
            </svg>
          </div>
          
          {label && (
            <label className={getLabelClasses()}>
              {label}
              {required && <span style={{ color: '#dc2626' }}> *</span>}
            </label>
          )}
        </div>

      {/* Multiple selection chips */}
      {multiple && selectedValues.length > 0 && (
        <div className={styles.chipContainer}>
          {selectedValues.map((selectedValue, index) => (
            <div key={getValue(selectedValue) || index} className={styles.chip}>
              {getLabel(selectedValue)}
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => handleRemoveChip(selectedValue)}
                tabIndex={-1}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
      
      {error && (
        <div className={styles.errorMessage}>{error}</div>
      )}
      {helperText && !error && (
        <div className={styles.helperText}>{helperText}</div>
      )}
      </div>
      
      <DropdownPortal />
    </>
  );
};

export default AutoSuggest;
